import clsx from "clsx";
import { useQueryState } from "nuqs";
import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import {
  ActivityPlayer,
  Loader,
  ProgressImage,
  useSlider,
} from "@transfr-inc/dashboard-components";
import { ActivityStatus } from "@transfr-inc/dashboard-components/utils";
import { useCourseActivityStatus } from "@transfr-inc/dashboard-sockets";

import {
  trackActivityPlayerViewed,
  trackButtonClicked,
  trackNextActivityButtonClicked,
} from "@lib/event-trackers";
import { useApiRequest } from "@lib/hooks";
import {
  createActivityPlayerLink,
  createMyWorkLink,
} from "@lib/utils/navigation.utils";
import { useCurrentUser, usePreviewClassroomModeEnabled } from "@queries";
import { useContainer } from "@src/contexts/container-context";

import {
  formatActivityPlayerData,
  getCredentialTypeByKey,
  getNextCredentialForLesson,
  mapActivitiesForActivityPlayer,
  mapCompletedAndLockedData,
  mapCredentialData,
  mapMasteries,
  progressPercent,
} from "@views/my-work/components/course/course.utils";

import CurriculumResourceActivityView from "./curriculum-resource-activity-view";
import IHActivityView from "./ih-activity-view";
import OohContent from "./ooh-activity-view";

import certificateIcon from "../../../../public/images/certificate-icon.png";
import microcredentialIcon from "../../../../public/images/microcredential-icon.png";

import { getContentTypeDisplayNameById } from "@transfr-inc/dashboard-components/curriculum";

import "./index.scss";

const socketUrl = process.env.SOCKET_BRIDGE_URL;

const ActivityView = () => {
  const { courseService, insightsService } = useContainer();
  const currentUser = useCurrentUser();
  const { token } = currentUser || {};
  const { lessonId } = useParams();

  const userId = currentUser?.userId;
  const history = useHistory();
  const location = useLocation();

  const isPreviewClassroomEnabled = usePreviewClassroomModeEnabled();
  const [previewParam] = useQueryState("preview");
  const isPreviewMode = isPreviewClassroomEnabled && !!previewParam;

  const courseId = location.state?.courseId;
  const classroomId = location.state?.classroomId;
  const unitId = location.state?.unitId;
  const prerequisitesEnabled =
    location.state?.prerequisitesEnabled && !isPreviewMode;
  const activityIndex = location.state?.activityIndex;
  const courseData = location.state?.courseData;

  const [loading, setLoading] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const { response: lessonMastery, loading: loadingMastery } = useApiRequest(
    () => insightsService.getLessonMastery(userId, courseId, lessonId)
  );

  const {
    response: activities,
    loading: loadingLesson,
    sendRequest: getLessonDetails,
  } = useApiRequest(
    () =>
      courseService.getLessonDetails(lessonId, currentUser?.organizationCode),
    false
  );

  const { breadcrumb, nextLessonId, nextLessonUnitId } =
    formatActivityPlayerData(courseData, lessonId);

  const {
    loading: loadingActivityDependencies,
    response: activityDependencies,
  } = useApiRequest(() => courseService.getActivityDependencies(courseId));

  const {
    response: courseCredentials,
    loading: courseCredentialsLoading,
    sendRequest: getCourseCredentials,
  } = useApiRequest(() => courseService.getCourseCredentials(courseId), false);

  const {
    response: courseCredentialsSummaryStats,
    loading: courseCredentialsSummaryStatsLoading,
  } = useApiRequest(() =>
    insightsService.getCourseCredentialsSummaryStats(classroomId)
  );

  useEffect(() => {
    if (
      loadingLesson ||
      loadingMastery ||
      loadingActivityDependencies ||
      courseCredentialsLoading ||
      courseCredentialsSummaryStatsLoading
    ) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [
    loadingLesson,
    loadingMastery,
    loadingActivityDependencies,
    courseCredentialsLoading,
    courseCredentialsSummaryStatsLoading,
  ]);

  useEffect(() => {
    if (lessonId) {
      getLessonDetails();
    }
    if (courseId) {
      getCourseCredentials();
    }
  }, [lessonId, courseId]);

  const lesson = { id: lessonId, unitId: unitId };

  const completionMap = courseData?.completionMap;

  const credentialData = useMemo(() => {
    if (!courseCredentials || !courseData || !courseCredentialsSummaryStats)
      return { mainCredential: null, courseInfo: [] };

    const { mainCredential, courseInfo } = mapCredentialData(
      courseCredentials,
      courseData,
      courseCredentialsSummaryStats
    );

    return {
      mainCredential,
      courseInfo,
    };
  }, [courseCredentials, courseData, courseCredentialsSummaryStats]);

  const nextCredentialData = getNextCredentialForLesson(
    lesson,
    courseData,
    credentialData
  );

  const [lessonCompletedCount, setLessonCompletedCount] = useState(0);
  const [mappedLesson, setMappedLesson] = useState([]);

  // Fix mappedCredentialData to actually display credential data properly, not just try to sort keys of an array-like object.
  const mappedCredentialData = useMemo(() => {
    if (!Array.isArray(nextCredentialData) || !nextCredentialData.length)
      return null;

    // Sort credentials by type: course > unit > lesson (numerically: 1, 2, 3)
    const sortedCredentials = [...nextCredentialData].sort(
      (a, b) => getCredentialTypeByKey(a.type) - getCredentialTypeByKey(b.type)
    );

    const getImage = (key) => {
      const imageType = getCredentialTypeByKey(key);
      if (imageType === 2) {
        return microcredentialIcon;
      } else if (imageType === 3) {
        return microcredentialIcon;
      } else {
        return certificateIcon;
      }
    };

    return sortedCredentials.map((cred) => {
      const progressMap = cred.progressMap || { numerator: 0, denominator: 0 };
      const numerator = progressMap.numerator + lessonCompletedCount;
      const denominator = progressMap.denominator;
      const progress = progressPercent({ numerator, denominator });
      const image = getImage(cred.type);

      return (
        cred?.id && (
          <div key={cred.id} className="image-container">
            <ProgressImage
              strokeWidth={9}
              progress={progress}
              defaultImage={image}
              defaultImage2x={image}
              imageAlt={"Certificate"}
              tooltipText={`${numerator} / ${denominator}`}
              type={getCredentialTypeByKey(cred.type)}
              trail="#ABB9C4"
            />
            <div className="credential-title">{cred.title}</div>
          </div>
        )
      );
    });
  }, [nextCredentialData, lessonCompletedCount]);

  const activityStatus = useCourseActivityStatus(
    socketUrl,
    token,
    courseId,
    lessonId,
    classroomId,
    userId
  );

  const oohLaunchParameters = {
    actor: {
      account: {
        homePage: `${window.location.protocol}//${window.location.hostname}`,
        name: `${userId}`,
      },
      objectType: "Agent",
    },
    endpoint: process.env.LRS_ENDPOINT + "xapi/",
    auth: "Bearer " + token,
  };

  useEffect(() => {
    if (
      activities &&
      !loadingLesson &&
      lessonMastery &&
      !loadingMastery &&
      activityDependencies
    ) {
      const masteries = mapMasteries(lessonMastery);
      const dependencies = activityDependencies ?? {};

      const completedAndLockedData = mapCompletedAndLockedData(
        {
          lessons: [{ activities }],
          prerequisitesEnabled: prerequisitesEnabled,
        },
        masteries,
        dependencies ?? {},
        courseId
      );

      // Create content creation functions
      const createOohContent = (enhancedLaunchParameters, updatedActivity) => (
        <OohContent
          oohLaunchParameters={enhancedLaunchParameters}
          data={updatedActivity}
        />
      );

      const createIhContent = (updatedActivity, courseId, classroomId) => (
        <IHActivityView
          title={updatedActivity.title}
          courseId={courseId}
          description={updatedActivity.description}
          timeToComplete={updatedActivity.estDuration}
          status={updatedActivity.status} // activityStatus for navigation
          masteryStatus={updatedActivity.masteryStatus} // masteryStatus for notification
          timeSpent={updatedActivity.timeSpent}
          completionDate={updatedActivity.simCompletionDate}
          latestTimestamp={updatedActivity.latestTimestamp}
          masteryScore={updatedActivity.masteryScore}
          learningExperienceId={updatedActivity.simulationId}
          simulationId={updatedActivity.simulationId}
          appName={updatedActivity.appName}
          categoryCode={updatedActivity.categoryCode}
          moduleId={updatedActivity.moduleId}
          classroomId={classroomId}
          thumbnail_image_path={updatedActivity.thumbnailImagePath}
        />
      );

      const createCurriculumResourceContent = (updatedActivity) => (
        <CurriculumResourceActivityView
          data={updatedActivity}
          courseId={courseId}
          classroomId={classroomId}
          lessonId={lessonId}
          unitId={unitId}
          onMarkDone={() => {
            setLessonCompletedCount((prev) => prev + 1);
            setMappedLesson((prev) => {
              const idx = prev.findIndex((a) => a.id === updatedActivity.id);
              if (idx === -1 || prev[idx].status === ActivityStatus.Complete)
                return prev;
              const completedActivityData = { ...updatedActivity, isComplete: true };
              return prev.map((a, i) => {
                if (a.id === updatedActivity.id)
                  return {
                    ...a,
                    status: ActivityStatus.Complete,
                    isComplete: true,
                    children: () => createCurriculumResourceContent(completedActivityData),
                  };
                if (i === idx + 1 && a.status === ActivityStatus.Locked)
                  return { ...a, status: ActivityStatus.Incomplete };
                return a;
              });
            });
          }}
        />
      );

      const updatedLesson = mapActivitiesForActivityPlayer(
        activities,
        masteries,
        completedAndLockedData,
        {
          courseId,
          classroomId,
          oohLaunchParameters,
          createOohContent,
          createIhContent,
          createCurriculumResourceContent,
        }
      );

      setMappedLesson(updatedLesson);
    }
  }, [activities, lessonMastery, activityDependencies, lessonId]);

  useEffect(() => {
    if (!mappedLesson.length || !activityStatus) return;

    let didComplete = false;
    const updated = mappedLesson.map((activity, index) => {
      if (activityStatus.activity_id === activity.id && activityStatus.complete) {
        didComplete = true;
        return { ...activity, status: ActivityStatus.Complete };
      }

      const prevActivity = mappedLesson[index - 1];
      if (
        prevActivity &&
        activityStatus.activity_id === prevActivity.id &&
        activityStatus.complete &&
        activity.status === ActivityStatus.Locked
      ) {
        return { ...activity, status: ActivityStatus.Incomplete };
      }

      return activity;
    });

    if (didComplete) {
      setLessonCompletedCount((prev) => prev + 1);
    }
    setMappedLesson(updated);
  }, [activityStatus]);

  const { endText, targetId } = useMemo(() => {
    let buttonText = "Complete Lesson";
    let id = lessonId;
    if (!completionMap) {
      return { endText: buttonText, targetId: id };
    } else {
      if (completionMap?.courseComplete === lessonId) {
        buttonText = "Complete Course";
      } else if (completionMap[unitId] === lessonId) {
        buttonText = "Complete Unit";
      } else {
        buttonText = "Complete Lesson";
      }

      if (
        buttonText === "Complete Lesson" ||
        buttonText === "Complete Unit" ||
        buttonText === "Complete Course"
      ) {
        id =
          buttonText === "Complete Lesson"
            ? lessonId
            : buttonText === "Complete Unit"
              ? unitId
              : courseId;
      }
      return { endText: buttonText, targetId: id };
    }
  }, [completionMap, courseId, lessonId, unitId]);

  const totalSlides = mappedLesson?.length;

  const slider = useSlider({ totalSlides, initialIndex: activityIndex });

  const [sliderPositioned, setSliderPositioned] = useState(
    !activityIndex || activityIndex === 0
  );

  useEffect(() => {
    if (!sliderPositioned && !loading) {
      const id = requestAnimationFrame(() => setSliderPositioned(true));
      return () => cancelAnimationFrame(id);
    }
  }, [loading, sliderPositioned]);

  const handleNext = () => {
    trackNextActivityButtonClicked(targetId, classroomId);
    slider.nextSlide();
  };

  const handlePrev = () => {
    slider.previousSlide();
  };

  const handleLinkClick = () => {
    history.push(
      createMyWorkLink({
        ...(isPreviewMode && { search: { preview: classroomId } }),
        state: { courseId, courseData },
      })
    );
  };

  useEffect(() => {
    if (mappedLesson.length > 0 && slider.currentIndex !== undefined) {
      const currentActivity = mappedLesson[slider.currentIndex];
      if (currentActivity?.id && currentActivity?.title) {
        trackActivityPlayerViewed(
          currentActivity.id,
          currentActivity.title,
          classroomId,
          getContentTypeDisplayNameById(currentActivity.contentType)
        );
      }
    }
  }, [mappedLesson, slider.currentIndex, classroomId]);

  const handleComplete = async () => {
    const completedCredential = Object.values(nextCredentialData).find(
      (cred) => cred.id === targetId
    );

    if (!isPreviewMode) {
      trackButtonClicked(targetId, endText, { classroomId });
    }

    if (nextLessonId) {
      history.push(
        createActivityPlayerLink({
          lessonId: nextLessonId,
          ...(isPreviewMode && { search: { preview: classroomId } }),
          state: {
            classroomId,
            courseId,
            unitId: nextLessonUnitId || unitId,
            activityIndex: 0,
            prerequisitesEnabled,
            courseData,
            activityDependencies,
          },
        })
      );
    } else {
      history.push(
        createMyWorkLink({
          ...(isPreviewMode && { search: { preview: classroomId } }),
          state: {
            courseId,
            courseData,
          },
        })
      );
    }
  };

  // UPDATE BREADCRUMB TO INCLUDE RETURN TO MAIN TAB PAGE

  return (
    <div
      className={clsx(
        "trainee-activity-view",
        !sliderPositioned && "slider-positioning"
      )}
    >
      {loading ? (
        <Loader />
      ) : (
        <ActivityPlayer
          slideData={mappedLesson}
          totalSlides={totalSlides}
          handleLinkClick={handleLinkClick}
          links={breadcrumb}
          slider={slider}
          handleNext={handleNext}
          handlePrev={handlePrev}
          credentials={mappedCredentialData}
          handleComplete={handleComplete}
          endText={endText}
          renderIndividually
          prerequisitesEnabled={prerequisitesEnabled}
          isFullScreen={isFullScreen}
          setIsFullScreen={setIsFullScreen}
          isPreviewMode={isPreviewMode}
        />
      )}
    </div>
  );
};

export default ActivityView;
